<script lang="ts">
	//Stores
	import { user_data, user_settings } from 'src/stores/account/user';
	import { app } from 'src/stores/session/native-bridge';
	import { routeParams } from 'src/stores/ui/viewport';
	import { includeDeviceModal } from 'src/stores/features/inclusion';
	//Design System Components
	import { IconButton } from '@Shelly/shelly-design-system/index';
	//Helpers
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { type DrodpwonMenuSubmenuItemsTypes } from 'src/helpers/main_menu';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { homes, currentUnitId } from 'src/stores/account/home';
	//Components
	import AddActionsModal from './AddActionsModal.svelte';
	import AlarmAddActionModal from './Wizards/AlarmAddActionModal.svelte';
	import GroupAddActionModal from './Wizards/GroupAddActionModal.svelte';
	import RoomAddActionModal from './Wizards/RoomAddActionModal.svelte';
	import SceneAddActionModal from './Wizards/SceneAddActionModal.svelte';
	import AddActionDropdown from './AddActionDropdown.svelte';
	import EmptyDeviceList from '../Lists/EmptyDeviceList.svelte';
	import DashWidgetActionModal from './Wizards/DashWidgetActionModal.svelte';
	import DashAddEditActionModal from './Wizards/DashAddEditActionModal.svelte';
	import ThermostatAddActionModal from './Wizards/ThermostatAddActionModal.svelte';

	const ADD_ACTION_MODALS = {
		rooms: RoomAddActionModal,
		groups: GroupAddActionModal,
		scenes: SceneAddActionModal,
		thermostat: ThermostatAddActionModal,
		alarms: AlarmAddActionModal,
		newDash: DashAddEditActionModal,
		newDashWidget: DashWidgetActionModal,
	};

	const ACTION_MODALS_SKIP_ROUTE_PARAMS = ['rooms', 'alarm', 'newDash'];

	let showAddActionsDropdown = false;
	let showAddActionsModal = false;
	let windowInnerWidth: number;
	let emptyDeviceListShow = false;
	let showActionFlowModal = false;
	let selectedMenuItem: DrodpwonMenuSubmenuItemsTypes | null = null;

	const onToggleAddActions = () => {
		if (isTablerOrMobile) {
			showAddActionsModal = !showAddActionsModal;
		} else {
			showAddActionsDropdown = !showAddActionsDropdown;
		}
	};

	const resetSelectedMenuItem = () => {
		selectedMenuItem = null;
	};

	const onInclDeviceProcess = () => {
		if (isActiveHomeShared($homes.list)) return;
		if ($app) {
			includeDeviceModal.update((state) => {
				return (state = { ...state, show: true });
			});
		} else {
			emptyDeviceListShow = true;
		}
	};

	const onClickDropdownMenuItem = (item: DrodpwonMenuSubmenuItemsTypes) => {
		selectedMenuItem = item;

		if (item.id === 9) {
			if (isActiveHomeShared($homes.list)) return;
			onInclDeviceProcess();
		} else {
			showActionFlowModal = true;
		}

		onToggleAddActions();
	};

	$: isTablerOrMobile = windowInnerWidth < SystemScreenBreakpoints.TABLET_MAX;
	/**
	 * Re-evaluate against the full active-home context, not just the homes-list
	 * identity. Switching from a shared home to an owned unit flips the
	 * sub-account flag (`user_settings.sub`) and `currentUnitId` (used by the
	 * `isActiveHomeShared` fallback), so referencing those stores keeps the
	 * control from getting stuck hidden on the owned unit.
	 */
	$: isSubAccountUser = ($user_settings, isSubAccount());
	$: isSharedHomeActive = ($currentUnitId, $user_data, isActiveHomeShared($homes.list));
	/**
	 * Owned homes keep the full add menu. Shared homes still expose the trigger,
	 * but the dropdown/modal restrict the options to dashboard actions only.
	 */
	$: showAddAction = !isSubAccountUser || isSharedHomeActive;
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

{#if showAddAction}
	<AddActionDropdown
		on:click-action={(e) => {
			const item = e.detail;
			onClickDropdownMenuItem(item);
		}}
		bind:showAddActionsDropdown
	>
		<IconButton
			aria-label="Add actioction btn"
			isActive={showAddActionsDropdown || emptyDeviceListShow || $includeDeviceModal.show}
			slot="trigger"
			shape="circle"
			icon="fas fa-plus"
			on:click={onToggleAddActions}
		/>
	</AddActionDropdown>
{/if}

<AddActionsModal
	bind:showAddActionsModal
	on:click-action={(e) => {
		onClickDropdownMenuItem(e.detail);
	}}
/>

{#if !$app}
	<EmptyDeviceList hideMessage bind:showModal={emptyDeviceListShow} />
{/if}

{#if showActionFlowModal}
	<svelte:component
		this={ADD_ACTION_MODALS[selectedMenuItem?.key]}
		bind:showModal={showActionFlowModal}
		on:close={resetSelectedMenuItem}
		{...selectedMenuItem?.key === 'newDashWidget' ? { isAddWizard: true } : {}}
		{...ACTION_MODALS_SKIP_ROUTE_PARAMS.includes(selectedMenuItem?.key)
			? {}
			: { routeParams: $routeParams }}
	/>
{/if}
